import { ComponentChildren, h, Ref } from "preact";

import { useState } from "preact/hooks";

import { PLATFORM, PlatformContextMap } from "../../../foundation/enums";
import CSSProperties from "../../../foundation/model/CSSProperties";
import classname from "../../../foundation/utils/classname";

import IconPin from "../Icons/IconPin";
import IconSettings from "../Icons/IconSettings";
import IconUnpin from "../Icons/IconUnpin";
import TeyzeTooltip, { TeyzeTooltipProps } from "../Teyze/TeyzeTooltip";
import BarrelThumbnail from "../BarrelThumbnail";
import Button, { ButtonVariant } from "../Button";
import HighlightText from "../HighlightText";
import OnlyIconButton from "../OnlyIconButton";
import Tooltip, { TooltipProps } from "../Tooltip";

import {
    BARREL_CARD_WIDTH,
    BARREL_CARD_HEIGHT,
    BARREL_CARD_THUMBNAIL_WIDTH,
    BARREL_CARD_THUMBNAIL_HEIGHT
} from "./specs";

import styles from "./BarrelCard.module.css";

type ActionButtonTeyzeProps = Omit<TeyzeTooltipProps, "targetContent">;

interface Props {
    class?: string;
    name: string;
    type?: PLATFORM;
    date?: ComponentChildren;
    thumbnailUrl?: string;
    thumbnailColorSeed?: string;
    highlighted?: boolean;
    highlightOnHover?: boolean; // TODO-yahya: Remove this logic if won't be used
    highlightColor?: "blue" | "yellow";
    archived?: boolean;
    pinned?: boolean;
    showActionButtons?: boolean;
    showJoinButton?: boolean;
    showPinButton?: boolean;
    actionsDisplayMode?: "always" | "hover";
    statusIndicator?: ComponentChildren | null;
    statusIndicatorDisplayMode?: "always" | "hover";
    contextMenu?: ComponentChildren | null;
    filterValue?: string;
    baseRef?: Ref<HTMLDivElement>;
    settingsButtonRef?: Ref<HTMLDivElement>;
    pinButtonTeyzeProps?: ActionButtonTeyzeProps;
    settingsButtonTeyzeProps?: ActionButtonTeyzeProps;
    pinButtonTooltipProps?: TooltipProps;
    hasDuplicateName?: boolean;
    onClick?: (ev: MouseEvent) => void;
    onJoinButtonClick?: (ev: Event) => void;
    onPinButtonClick?: (ev: Event) => void;
    onSettingsButtonClick?: (ev: Event) => void;
    onStatusIndicatorClick?: (ev: Event) => void;
}

function BarrelCard({
    class: cssClass,
    name,
    type,
    date,
    thumbnailUrl,
    thumbnailColorSeed,
    highlighted,
    highlightOnHover,
    highlightColor,
    archived,
    pinned,
    showActionButtons = true,
    showJoinButton,
    showPinButton,
    actionsDisplayMode = "hover",
    statusIndicator,
    statusIndicatorDisplayMode = "hover",
    contextMenu,
    filterValue,
    baseRef,
    settingsButtonRef,
    pinButtonTeyzeProps,
    settingsButtonTeyzeProps,
    pinButtonTooltipProps,
    hasDuplicateName,
    onClick,
    onJoinButtonClick,
    onPinButtonClick,
    onSettingsButtonClick,
    onStatusIndicatorClick
}: Props) {
    const style = {
        "--barrel-card-width": `${BARREL_CARD_WIDTH}px`,
        "--barrel-card-height": `${BARREL_CARD_HEIGHT}px`,
        "--barrel-card-thumbnail-width": `${BARREL_CARD_THUMBNAIL_WIDTH}px`,
        "--barrel-card-thumbnail-height": `${BARREL_CARD_THUMBNAIL_HEIGHT}px`
    } as CSSProperties;

    if (archived) {
        style["--barrel-card-highlight-color"] = "var(--control-default)";
    } else if (highlightColor) {
        style["--barrel-card-highlight-color"] = `var(--marker-${highlightColor})`;
    }

    const [pinnedPinButtonHover, setPinnedPinButtonHover] = useState<boolean>(false);

    function handlePinButtonMouseEnter() {
        setPinnedPinButtonHover(true);
    }

    function handlePinButtonMouseLeave() {
        setPinnedPinButtonHover(false);
    }

    function renderPinButton() {
        const rawPinButton = pinned ? (
            <OnlyIconButton
                class={styles.pinButton}
                icon={pinnedPinButtonHover ? IconUnpin : IconPin}
                selected={!pinnedPinButtonHover}
                onClick={onPinButtonClick}
                onMouseEnter={handlePinButtonMouseEnter}
                onMouseLeave={handlePinButtonMouseLeave}/>
        ) : (
            <OnlyIconButton
                class={styles.pinButton}
                icon={IconPin}
                selected={pinned}
                onClick={onPinButtonClick}/>
        );

        const pinButton = pinButtonTooltipProps
            ? (
                <Tooltip {...pinButtonTooltipProps}>
                    {rawPinButton}
                </Tooltip>
            )
            : rawPinButton;

        if (pinButtonTeyzeProps) {
            return (
                <TeyzeTooltip {...pinButtonTeyzeProps} >
                    {pinButton}
                </TeyzeTooltip>
            );
        }

        return pinButton;
    }

    function renderActionButtons() {
        return (
            <div class={styles.actionsOverlay}>
                <div class={styles.actionButtons}>
                    {showPinButton && renderPinButton()}
                    <div ref={settingsButtonRef}>
                        <TeyzeTooltip {...settingsButtonTeyzeProps}>
                            <OnlyIconButton
                                class={styles.settingsButton}
                                icon={IconSettings}
                                onClick={onSettingsButtonClick}/>
                        </TeyzeTooltip>
                    </div>
                </div>
            </div>
        );
    }

    function renderJoinButton() {
        return (
            <div class={styles.joinOverlay}>
                <Button
                    class={styles.joinButton}
                    variant={ButtonVariant.Secondary}
                    onClick={onJoinButtonClick}>
                    <span>
                        Not joined yet, <span class={styles.joinNowText}>join now</span>.
                    </span>
                </Button>
            </div>
        );
    }

    function renderStatusIndicator() {
        return (
            <div class={classname(styles.statusIndicatorOverlay, {
                [styles.showOnHover]: statusIndicatorDisplayMode === "hover"
            })}
            onClick={onStatusIndicatorClick}>
                {statusIndicator}
            </div>
        );
    }

    function renderContextMenu() {
        return contextMenu;
    }

    return (
        <div
            class={classname(styles.barrelCard, cssClass, {
                [styles.showActionsOnHover]: actionsDisplayMode === "hover",
                [styles.highlighted]: highlighted,
                [styles.highlightOnHover]: highlightOnHover,
                [styles.archived]: archived,
                [styles.pinned]: pinned,
                [styles.showJoinButton]: showJoinButton,
                [styles.contextMenuOpen]: !!contextMenu
            })}
            style={style}
            ref={baseRef}
            onClick={onClick}>
            <div class={styles.content}>
                <div class={styles.thumbnailAndActionsWrapper}>
                    <BarrelThumbnail
                        class={styles.thumbnail}
                        width={BARREL_CARD_THUMBNAIL_WIDTH}
                        height={BARREL_CARD_THUMBNAIL_HEIGHT}
                        url={thumbnailUrl}
                        colorSeed={thumbnailColorSeed}/>
                    {statusIndicator && renderStatusIndicator()}
                    {showJoinButton && renderJoinButton()}
                </div>
                <div class={styles.info}>
                    <HighlightText
                        class={classname("heading-4 medium", styles.name)}
                        text={`${hasDuplicateName && type ? `[${PlatformContextMap[type].name}] ` : ""}${name}`}
                        filterValue={filterValue}/>
                    <div class={classname("caption-1", styles.date)}>
                        {date}
                    </div>
                </div>
            </div>
            {showActionButtons && renderActionButtons()}
            <div class={styles.highlightOverlay}>
                <div class={styles.highlightBody}/>
            </div>
            {renderContextMenu()}
        </div>
    );
}

export default BarrelCard;
export { Props as BarrelCardProps };
